'use client'

import ChevronLeft from '@/components/icons/chevron-left'
import CogIcon from '@/components/icons/cog'
import CrossIcon from '@/components/icons/cross'
import DollarSign from '@/components/icons/dollar-sign'
import HamburgerIcon from '@/components/icons/hamburger'
import ManageLeagueIcon from '@/components/icons/manage-league-icon'
import QuillIcon from '@/components/icons/quill-icon'
import { cn } from '@/lib/utils'
import { MicIcon, PlusIcon, TvIcon, UserIcon } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'

export default function ProfileManagerSidebar({ user }) {
  const [mobileIsOpen, setMobileIsOpen] = useState(false)
  const path = usePathname().split('/')

  /*
   * Logic to determine which profiles to show
   */
  const showBroadcaster = !!user?.broadcasterConfig
  const showCommentator = !!user?.commentatorConfig
  const showLiveryDesigner = !!user?.liveryDesignerConfig

  const showAddRole =
    !showBroadcaster || !showCommentator || !showLiveryDesigner

  const profiles = [
    {
      name: 'Driver',
      icon: <UserIcon className="size-6" />,
      href: '/profile-manager/driver/calendar',
      pathCheck: 'driver',
      show: true,
    },
    {
      name: 'Livery Designer',
      icon: <QuillIcon className="size-6" />,
      href: '/profile-manager/liveries/overview',
      pathCheck: 'liveries',
      show: showLiveryDesigner,
    },
    {
      name: 'Broadcaster',
      icon: <TvIcon className="size-6" />,
      href: `/profile-manager/broadcaster`,
      pathCheck: 'broadcaster',
      show: showBroadcaster,
    },
    {
      name: 'Commentator',
      icon: <MicIcon className="size-6" />,
      href: `/profile-manager/commentator`,
      pathCheck: 'commentator',
      show: showCommentator,
    },
  ]

  return (
    <>
      <button
        onClick={() => setMobileIsOpen(!mobileIsOpen)}
        className="absolute right-0 top-0 z-20 mr-6 mt-[100px] stroke-shades-0 desktop-xs:hidden desktop-sm:z-0"
      >
        <div
          className={cn(
            'absolute left-[-14px] top-[-18px] z-0 h-full min-h-full w-full rounded-full p-7',
            mobileIsOpen ? 'bg-neutral-blue-800' : 'bg-neutral-blue-900',
          )}
        ></div>
        {mobileIsOpen ? (
          <CrossIcon className="relative z-40 mt-[-3px] size-7 stroke-2" />
        ) : (
          <HamburgerIcon className="relative z-40 size-7 stroke-2" />
        )}
      </button>
      <div
        className={cn(
          'no-scrollbar fixed left-0 top-0 z-10 h-full min-h-screen w-screen overflow-y-auto bg-neutral-blue transition-transform duration-300 desktop-xs:relative desktop-xs:w-fit desktop-xs:min-w-[280px] desktop-xs:translate-x-0',
          mobileIsOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Link
          onClick={() => setMobileIsOpen(false)}
          href="/"
          className="mt-6 h-auto w-[280px]"
        >
          <div className="semibold-caps group flex items-center gap-1 px-6 pt-6 align-middle font-inter text-footer text-neutral-blue-500 transition-all ease-in-out hover:text-shades-0 group-hover:text-white">
            <ChevronLeft className="stroke-3 size-4 stroke-neutral-blue-500 transition-all ease-in-out group-hover:stroke-white" />
            Back to Grid Finder
          </div>
        </Link>
        <div className="z-50 w-full py-6 *:px-6 desktop-xs:fixed desktop-xs:w-[280px]">
          <div className="relative ml-6 aspect-[5.52] w-[65%]">
            <Link
              onClick={() => setMobileIsOpen(false)}
              href="/profile-manager/driver/calendar"
            >
              <Image
                src="/img/profile-manager/logo.webp"
                alt="Grid Finder - The Ultimate Sim Racing Platform"
                fill
                priority={true}
              />
            </Link>
          </div>
          <p className="mt-10 p-1 text-p-2 font-semibold text-neutral-blue-400">
            My Profile
          </p>
          <ul
            tabIndex={0}
            className="mx-[-24px] my-4 flex w-full flex-col gap-5"
          >
            {profiles
              .filter((p) => p.show)
              .map((p) => (
                <Link
                  key={p.name}
                  onClick={() => setMobileIsOpen(false)}
                  href={p.href}
                >
                  <li
                    className={cn(
                      'flex h-8 w-full items-center gap-3 border-l-4 px-6 pl-5 font-semibold transition-all hover:stroke-neutral-blue-100 hover:text-neutral-blue-100',
                      path[2] === p.pathCheck
                        ? 'border-primary stroke-shades-0 text-shades-0'
                        : 'border-transparent stroke-neutral-blue-500 text-neutral-blue-500',
                    )}
                  >
                    {p.icon}
                    <span className="pb-px text-p-2">{p.name}</span>
                  </li>
                </Link>
              ))}
          </ul>
          {showAddRole && (
            <Link
              onClick={() => setMobileIsOpen(false)}
              href="/profile-manager/add-role"
              className="mx-6 mb-6 mt-6 block rounded-sm border border-dashed border-neutral-blue-500 py-4 font-semibold transition-all hover:border-neutral-blue-400 hover:bg-neutral-blue-700"
            >
              <div className="m-auto flex w-fit items-center gap-2 pr-3 text-p-2 text-neutral-blue-300">
                <PlusIcon className="h-4 w-4" /> Add a Role
              </div>
            </Link>
          )}
          <div className="mx-6 mt-4 h-px bg-neutral-blue-700/90" />
          <Link
            onClick={() => setMobileIsOpen(false)}
            href={'/manage'}
            className="mt-3 block"
          >
            <li
              className={cn(
                'flex h-8 w-full items-center gap-3 border-transparent stroke-neutral-blue-500 text-[17px] font-semibold text-neutral-blue-500 transition-all hover:stroke-neutral-blue-100 hover:text-neutral-blue-100',
              )}
            >
              <ManageLeagueIcon className="size-6" />
              <span className="pb-px text-p-2">Manager</span>
            </li>
          </Link>
          <div className="mx-6 mb-4 mt-3 h-px bg-neutral-blue-700/90" />
          <p className="mt-4 p-1 text-p-2 font-semibold text-neutral-blue-400">
            Settings
          </p>
          <Link
            onClick={() => setMobileIsOpen(false)}
            href={`/profile-manager/order-history`}
          >
            <li
              className={cn(
                'flex h-8 w-full items-center gap-3 border-l-4 px-6 pl-5 align-middle text-[17px] font-semibold transition-all hover:stroke-neutral-blue-100 hover:text-neutral-blue-100',
                path.includes('order-history')
                  ? 'border-primary stroke-shades-0 text-shades-0'
                  : 'border-transparent stroke-neutral-blue-500 text-neutral-blue-500',
              )}
            >
              <DollarSign className="size-6 stroke-1" />
              <span className="pb-px text-p-2">Order History</span>
            </li>
          </Link>
          <Link
            onClick={() => setMobileIsOpen(false)}
            href={`/profile-manager/settings/general`}
          >
            <li
              className={cn(
                'flex h-8 w-full items-center gap-3 border-l-4 px-6 pl-5 text-[17px] font-semibold transition-all hover:stroke-neutral-blue-100 hover:text-neutral-blue-100',
                path.includes('settings')
                  ? 'border-primary stroke-shades-0 text-shades-0'
                  : 'border-transparent stroke-neutral-blue-500 text-neutral-blue-500',
              )}
            >
              <CogIcon className="size-6 stroke-[1.4px]" />
              <span className="pb-px text-p-2">Settings</span>
            </li>
          </Link>
        </div>
      </div>
    </>
  )
}
